import { useEffect, useRef, useState } from 'react'
import { createVault } from './api'
import { strength, vault, type VaultMeta } from './vault'
import { toast } from './toast'

/**
 * Shown on demand (not a gate) when an admin reveals or sets a password and the
 * vault key is not in memory. `setup` mode when no master password exists yet;
 * `unlock` mode otherwise.
 */
export function LockModal({
  meta,
  onClose,
  onVaultCreated,
}: {
  meta: VaultMeta
  onClose: () => void
  onVaultCreated: () => void
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const mode = meta.exists ? 'unlock' : 'setup'
  const [pw, setPw] = useState('')
  const [pw2, setPw2] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const d = ref.current
    if (d && !d.open) d.showModal()
  }, [])

  async function submit() {
    setErr('')
    if (mode === 'setup') {
      if (pw.length < 10) return setErr('Use at least 10 characters.')
      if (pw !== pw2) return setErr('The two entries do not match.')
      setBusy(true)
      try {
        const { key, payload } = await vault.prepareSetup(pw)
        await createVault(payload)
        vault.adopt(key)
        toast('Master password set')
        onVaultCreated()
        onClose()
      } catch (e) {
        setErr((e as { message?: string }).message ?? 'Could not set the master password.')
      } finally {
        setBusy(false)
      }
    } else {
      setBusy(true)
      try {
        const ok = await vault.unlock(pw, meta)
        if (!ok) {
          setErr('That is not the master password.')
          setBusy(false)
          return
        }
        toast('Unlocked')
        onClose()
      } catch {
        setErr('Could not unlock.')
        setBusy(false)
      }
    }
  }

  const bars = strength(pw)

  return (
    <dialog
      ref={ref}
      onCancel={(e) => {
        e.preventDefault()
        vault.cancelUnlock()
        onClose()
      }}
    >
      <div className="form">
        <div className="mark" style={{ marginBottom: 14 }}>
          A
        </div>
        <h3>{mode === 'setup' ? 'Create a master password' : 'Enter the master password'}</h3>
        <p className="k">
          {mode === 'setup'
            ? 'This encrypts every stored password in your browser. It is never sent to the server and cannot be recovered — if it is lost, the stored passwords are gone.'
            : 'Needed to reveal or change a stored password. Held for 10 minutes, then asked again.'}
        </p>

        <div className="fld">
          <label htmlFor="mpw">Master password</label>
          <input
            id="mpw"
            type="password"
            autoComplete={mode === 'setup' ? 'new-password' : 'current-password'}
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && mode === 'unlock' && submit()}
          />
        </div>

        {mode === 'setup' && (
          <>
            <div className="meter" aria-hidden>
              {[0, 1, 2, 3].map((i) => (
                <i key={i} style={{ background: i < bars ? 'var(--gold)' : 'var(--line)' }} />
              ))}
            </div>
            <div className="fld" style={{ marginTop: 10 }}>
              <label htmlFor="mpw2">Repeat master password</label>
              <input
                id="mpw2"
                type="password"
                autoComplete="new-password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submit()}
              />
            </div>
          </>
        )}

        {err && <p className="req">{err}</p>}

        <div className="row">
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={() => {
              vault.cancelUnlock()
              onClose()
            }}
          >
            Cancel
          </button>
          <button type="button" className="btn p" disabled={busy || !pw} onClick={submit}>
            {busy ? 'Working…' : mode === 'setup' ? 'Set password' : 'Unlock'}
          </button>
        </div>
      </div>
    </dialog>
  )
}
